<script lang="ts">
import {
  countCardsByBurningTopic,
  uniqueCardTypes,
  uniqueDecks,
  uniqueSources,
  uniqueTags,
} from '$lib/filter';
import { defaultFilter, type PersistedFilterState } from '$lib/store';
import type { Card } from '$types/card';
import BurningTopicChips from './BurningTopicChips.svelte';

interface Props {
  cards: readonly Card[];
  filter: PersistedFilterState;
  onChange: (partial: Partial<PersistedFilterState>) => void;
}

const { cards, filter, onChange }: Props = $props();
let expanded = $state(false);

const counts = $derived(countCardsByBurningTopic(cards));
const decks = $derived(uniqueDecks(cards));
const types = $derived(uniqueCardTypes(cards));
const sources = $derived(uniqueSources(cards));
const tags = $derived(uniqueTags(cards));

const activeCount = $derived(
  filter.burningTopics.length +
    filter.decks.length +
    filter.cardTypes.length +
    filter.sources.length +
    filter.tags.length +
    (filter.searchQuery ? 1 : 0),
);

function toggleArray(arr: string[], v: string): string[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function reset(): void {
  // Voller Default inkl. mode:'review' — sonst bleibt ein zuvor gesetzter
  // Modus ('study'/'all') nach dem Reset bestehen.
  onChange(defaultFilter());
}

const MODE_LABELS: Record<PersistedFilterState['mode'], string> = {
  review: 'review · fällig',
  study: 'studium',
  all: 'alles',
};
// Eingeklappt zeigt die Zeile, WAS gerade gelernt wird — Chips + Detail-
// Filter erscheinen erst auf Klick, damit die Karte das Erste im Blick ist.
const summary = $derived(
  MODE_LABELS[filter.mode] + (activeCount > 0 ? ` · ${activeCount} filter aktiv` : ''),
);
</script>

<section class="filterbar">
  <div class="rule">
    <span class="rule-label">[ {summary} ]</span>
    <span class="rule-line"></span>
    <button type="button" class="rule-btn" onclick={() => (expanded = !expanded)}>
      {expanded ? '− schließen' : '+ filter'}
    </button>
  </div>

  {#if expanded}
    <BurningTopicChips
      selected={filter.burningTopics}
      {counts}
      onToggle={(id) => onChange({ burningTopics: toggleArray(filter.burningTopics, id) })} />

    <div class="filter-meta">
      <button
        type="button"
        class={'reset' + (activeCount === 0 ? ' is-disabled' : '')}
        disabled={activeCount === 0}
        onclick={reset}>
        ✕ reset · {activeCount} aktiv
      </button>
    </div>

    <div class="filterbar-adv">
      <div class="adv-row">
        <label class="adv-field">
          <span class="adv-k">modus</span>
          <select
            value={filter.mode}
            onchange={(e) => onChange({ mode: (e.currentTarget as HTMLSelectElement).value as PersistedFilterState['mode'] })}>
            <option value="review">review · fsrs-faellig</option>
            <option value="study">studium · alle gefilterten</option>
            <option value="all">alles</option>
          </select>
        </label>
        <label class="adv-field adv-search">
          <span class="adv-k">suche</span>
          <input
            type="search"
            placeholder="volltext · id · tag · deck …"
            value={filter.searchQuery}
            oninput={(e) => onChange({ searchQuery: (e.currentTarget as HTMLInputElement).value })} />
        </label>
        <label class="adv-field">
          <span class="adv-k">lernfeld</span>
          <select
            multiple
            size="3"
            onchange={(e) => onChange({ decks: [...(e.currentTarget as HTMLSelectElement).selectedOptions].map((o) => o.value) })}>
            {#each decks as d}<option value={d} selected={filter.decks.includes(d)}>{d}</option>{/each}
          </select>
        </label>
        <label class="adv-field">
          <span class="adv-k">karten-typ</span>
          <select
            multiple
            size="3"
            onchange={(e) => onChange({ cardTypes: [...(e.currentTarget as HTMLSelectElement).selectedOptions].map((o) => o.value) })}>
            {#each types as t}<option value={t} selected={filter.cardTypes.includes(t)}>{t}</option>{/each}
          </select>
        </label>
        <label class="adv-field">
          <span class="adv-k">quelle</span>
          <select
            multiple
            size="3"
            onchange={(e) => onChange({ sources: [...(e.currentTarget as HTMLSelectElement).selectedOptions].map((o) => o.value) })}>
            {#each sources as s}<option value={s} selected={filter.sources.includes(s)}>{s}</option>{/each}
          </select>
        </label>
        <label class="adv-field">
          <span class="adv-k">tag</span>
          <select
            multiple
            size="3"
            onchange={(e) => onChange({ tags: [...(e.currentTarget as HTMLSelectElement).selectedOptions].map((o) => o.value) })}>
            {#each tags as t}<option value={t} selected={filter.tags.includes(t)}>{t}</option>{/each}
          </select>
        </label>
      </div>
    </div>
  {/if}
</section>

<style>
  .filterbar { margin-top: 6px; }
  .rule {
    display: flex; align-items: center; gap: 10px;
    margin: 16px 0 8px;
    font-size: 11px; letter-spacing: 0.08em;
    color: var(--ink-3); text-transform: uppercase;
  }
  .rule-label { white-space: nowrap; font-weight: 600; }
  .rule-line { flex: 1; height: 0; border-top: 1px dashed var(--rule); opacity: 0.5; }
  .rule-btn {
    background: transparent; border: 1px solid var(--rule);
    padding: 3px 8px; font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--ink-2);
  }
  .rule-btn:hover { background: var(--paper-2); }

  .filter-meta { display: flex; justify-content: flex-end; margin-top: 4px; }
  .reset {
    border: 1px solid var(--r-again);
    background: transparent;
    color: var(--r-again);
    padding: 5px 12px;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .reset:not(:disabled):hover { background: var(--r-again); color: #fff; }
  .reset.is-disabled, .reset:disabled { opacity: 0.4; cursor: not-allowed; }

  .filterbar-adv {
    border: 1px dashed var(--rule);
    padding: 12px;
    margin-top: 8px;
    background: var(--paper-2);
  }
  .adv-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
  }
  .adv-field { display: flex; flex-direction: column; gap: 4px; }
  .adv-k { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }
  .adv-field select, .adv-field input {
    border: 1px solid var(--rule);
    background: var(--paper);
    color: var(--ink);
    padding: 6px 8px;
    font-family: var(--font-mono);
    font-size: 12px;
    border-radius: 0;
  }
  .adv-field select[multiple] { min-height: 70px; }
</style>
